import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";

import {
  getLeaveTypes,
  createLeaveType,
  updateLeaveType,
  toggleLeaveTypeStatus,
} from "../../../../../../services/attendanceService";
import CommonModal from "../../../../../../core/common/commonModal";
import { showBasicAlert } from "../../../../../../core/common/useAlert";

type LeavePolicy = {
  id: string;
  name: string;
  days: number;
  employees: string[];
};

type LeaveType = {
  id: number;
  name: string;
  description: string;
  category: "planned" | "unplanned" | "unpaid" | "other";
  isActive: boolean;
  eligibleGender: "male" | "female" | "any";
  eligibleEmploymentType: string;
  minimumServiceMonths: number;
  balance: number;
  requiresApproval: boolean;
  carryForwardAllowed: boolean;
  carryForwardLimit: number;
  isMonthlyAccrual: boolean;
  monthlyAllocation: number;
  monthlyLimit: number;
  monthlyCarryForward: boolean;
  companyId: number;
  companyType: string;
  policies: LeavePolicy[];
};

type SelectedCompany = {
  value: string;
  label: string;
  type: string;
} | null;

type LeaveTypesTabProps = {
  selectedCompany: SelectedCompany;
};

export const LeaveTypesTab: React.FC<LeaveTypesTabProps> = ({
  selectedCompany,
}) => {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [showLeaveTypeModal, setShowLeaveTypeModal] = useState(false);
  const [selectedLeaveType, setSelectedLeaveType] =
    useState<LeaveType | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const [formData, setFormData] = useState<Omit<any, "id" | "policies">>({
    name: "",
    description: "",
    category: "planned",
    isActive: true,
    eligibleGender: "any",
    eligibleEmploymentType: "any",
    minimumServiceMonths: 0,
    balance: 0,
    requiresApproval: true,
    carryForwardAllowed: false,
    carryForwardLimit: 0,
    isMonthlyAccrual: false,
    monthlyAllocation: 0,
    monthlyLimit: 0,
    monthlyCarryForward: false,
    companyId: 0,
    companyType: "",
  });

  useEffect(() => {
    if (selectedCompany && selectedCompany.value) {
      fetchCompanyLeaves(selectedCompany);
    } else {
      setLeaveTypes([]);
    }
  }, [selectedCompany]);

  const fetchCompanyLeaves = async (company: SelectedCompany) => {
    setIsLoading(true);
    try {
      const res = await getLeaveTypes(company);
      setLeaveTypes(res.data.data.leaveTypes || []);
    } catch (error) {
      showBasicAlert("Failed to load leave types");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleLeaveTypeStatusHandler = async (
    leaveTypeId: number,
    currentStatus: boolean,
  ) => {
    setIsProcessing(true);
    try {
      const data = await toggleLeaveTypeStatus(leaveTypeId, !currentStatus);
      if (data) {
        setLeaveTypes((prevTypes) =>
          prevTypes.map((type) =>
            type.id === leaveTypeId
              ? { ...type, isActive: !currentStatus }
              : type,
          ),
        );
        showBasicAlert(
          `Leave type ${currentStatus ? "disabled" : "enabled"} successfully`,
        );
      } else {
        showBasicAlert(`Something went wrong 101.`);
      }
    } catch (error) {
      showBasicAlert(
        `Failed to ${currentStatus ? "disable" : "enable"} leave type`,
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddLeaveType = () => {
    if (!selectedCompany || !selectedCompany.value) {
      showBasicAlert("Please select a company first");
      return;
    }

    setFormData({
      name: "",
      description: "",
      category: "planned",
      isActive: true,
      eligibleGender: "any",
      eligibleEmploymentType: "any",
      minimumServiceMonths: 0,
      balance: 0,
      requiresApproval: true,
      carryForwardAllowed: false,
      carryForwardLimit: 0,
      isMonthlyAccrual: false,
      monthlyAllocation: 0,
      monthlyLimit: 0,
      monthlyCarryForward: false,
      companyId: parseInt(selectedCompany.value),
      companyType: selectedCompany.type,
    });
    setIsEditing(false);
    setSelectedLeaveType(null);
    setShowLeaveTypeModal(true);
  };

  const handleEditLeaveType = (leaveType: LeaveType) => {
    setFormData({
      name: leaveType.name,
      description: leaveType.description,
      category: leaveType.category,
      isActive: leaveType.isActive,
      eligibleGender: leaveType.eligibleGender,
      eligibleEmploymentType: leaveType.eligibleEmploymentType,
      minimumServiceMonths: leaveType.minimumServiceMonths,
      balance: leaveType.balance,
      requiresApproval: leaveType.requiresApproval,
      carryForwardAllowed: leaveType.carryForwardAllowed,
      carryForwardLimit: leaveType.carryForwardLimit,
      isMonthlyAccrual: leaveType.isMonthlyAccrual || false,
      monthlyAllocation: leaveType.monthlyAllocation || 0,
      monthlyLimit: leaveType.monthlyLimit || 0,
      monthlyCarryForward: leaveType.monthlyCarryForward || false,
      companyId: leaveType.companyId,
      companyType: leaveType.companyType,
    });
    setIsEditing(true);
    setSelectedLeaveType(leaveType);
    setShowLeaveTypeModal(true);
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;

    setFormData((prev: any) => {
      const newData: any = {
        ...prev,
        [name]:
          type === "checkbox"
            ? (e.target as HTMLInputElement).checked
            : type === "number"
              ? parseInt(value) || 0
              : value,
      };

      if (name === "category" && value === "other") {
        newData.balance = -1;
        newData.carryForwardAllowed = false;
        newData.carryForwardLimit = 0;
        newData.isMonthlyAccrual = false;
        newData.monthlyAllocation = 0;
        newData.monthlyLimit = 0;
        newData.monthlyCarryForward = false;
      }

      if (
        name === "category" &&
        value !== "other" &&
        prev.category === "other"
      ) {
        newData.balance = 0;
      }

      return newData;
    });
  };

  const handleLeaveTypeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.isMonthlyAccrual) {
      if (formData.monthlyAllocation <= 0) {
        showBasicAlert("Monthly allocation must be greater than 0");
        return;
      }
      if (formData.monthlyLimit <= 0) {
        showBasicAlert("Monthly limit must be greater than 0");
        return;
      }
    }

    setIsProcessing(true);
    try {
      if (isEditing && selectedLeaveType) {
        await updateLeaveType(selectedLeaveType.id, formData);
        showBasicAlert("Leave type updated successfully");
      } else {
        await createLeaveType(formData);
        showBasicAlert("Leave type added successfully");
      }
      setShowLeaveTypeModal(false);
      fetchCompanyLeaves(selectedCompany);
    } catch (error) {
      showBasicAlert(`Failed to ${isEditing ? "update" : "add"} leave type`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <h5 className="mb-1">Leave Types</h5>
          <p className="text-muted mb-0 small">
            Configure the leave types available for this company.
          </p>
        </div>
        <Button
          onClick={handleAddLeaveType}
          className="btn btn-primary ls-add-btn d-flex align-items-center"
          disabled={!selectedCompany?.value || isLoading || isProcessing}
        >
          <i className="ti ti-circle-plus me-2" />
          Add Leave Type
        </Button>
      </div>

      {isLoading ? (
        <div className="row">
          {Array.from({ length: 6 }).map((_, i) => (
            <div className="col-xl-4 col-md-6 mb-3" key={i}>
              <div className="card leave-card skeleton">
                <div className="card-body">
                  <div className="sk-row w-50 mb-3" />
                  <div className="sk-row w-75 mb-2" />
                  <div className="sk-row w-25" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="row">
          {leaveTypes.length > 0 ? (
            leaveTypes.map((type) => (
              <div className="col-xl-4 col-md-6" key={type.id}>
                <div
                  className={`card leave-card ${
                    !type.isActive ? "is-inactive" : ""
                  }`}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="ls-icon me-3">
                          <i className="ti ti-beach" />
                        </div>
                        <div>
                          <h6 className="mb-1 d-flex align-items-center flex-wrap gap-2">
                            {type.name}
                            {!type.isActive && (
                              <span className="badge bg-danger-subtle text-danger border-0">
                                Inactive
                              </span>
                            )}
                            {type.isMonthlyAccrual && (
                              <span className="badge bg-info-subtle text-info border-0">
                                <i className="ti ti-calendar-stats me-1" />
                                Monthly
                              </span>
                            )}
                          </h6>
                          <small className="text-muted">
                            {type.isMonthlyAccrual
                              ? `${type.monthlyAllocation} per month (max ${type.monthlyLimit}/month)`
                              : `${type.balance} days total`}
                          </small>
                        </div>
                      </div>

                      <div className="d-flex align-items-center">
                        <div className="form-check form-check-md form-switch me-1 ls-switch">
                          <label className="form-check-label">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={type.isActive}
                              onChange={() =>
                                toggleLeaveTypeStatusHandler(
                                  type.id,
                                  type.isActive,
                                )
                              }
                              disabled={isProcessing}
                            />
                          </label>
                        </div>

                        <Button
                          variant="link"
                          className="ls-icon-btn ms-2"
                          title="Edit"
                          onClick={() => handleEditLeaveType(type)}
                          disabled={isProcessing}
                        >
                          <i className="ti ti-edit" />
                        </Button>
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-2">
                      {type.carryForwardAllowed ? (
                        <span className="badge badge-success border-0">
                          <i className="ti ti-arrow-forward-up me-1" />
                          Carry Forward {type.carryForwardLimit}
                        </span>
                      ) : (
                        <span className="badge badge-success border-0">
                          <i className="ti ti-arrow-forward-up me-1" />
                          Carry Forward 0
                        </span>
                      )}
                      {type.monthlyCarryForward ? (
                        <span className="badge badge-info border-0">
                          <i className="ti ti-calendar-repeat me-1" />
                          Monthly Carry Forward
                        </span>
                      ) : (
                        <span className="badge badge-danger border-0">
                          <i className="ti ti-calendar-repeat me-1" />
                          Not Carry Forward
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="col-12">
              <div className="card border-0 ls-empty">
                <div className="card-body text-center py-5">
                  <div className="ls-empty__icon mb-3">
                    <i className="ti ti-leaf" />
                  </div>
                  <h5 className="mb-1">No leave types found</h5>
                  <p className="text-muted mb-3">
                    Please select a company or add new leave types.
                  </p>
                  <Button
                    onClick={handleAddLeaveType}
                    className="btn btn-primary d-inline-flex align-items-center"
                    disabled={!selectedCompany?.value || isProcessing}
                  >
                    <i className="ti ti-circle-plus me-2" />
                    Add Leave Type
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <CommonModal
        id="leave-type-modal"
        title={isEditing ? "Edit Leave Type" : "Add New Leave Type"}
        visible={showLeaveTypeModal}
        onClose={() => !isProcessing && setShowLeaveTypeModal(false)}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowLeaveTypeModal(false)}
              disabled={isProcessing}
              className="m-2"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleLeaveTypeSubmit}
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : isEditing ? "Update" : "Save"}
            </Button>
          </>
        }
      >
        <form onSubmit={handleLeaveTypeSubmit} className="leave-type-form">
          <div className="section-card">
            <div className="section-head">
              <i className="ti ti-building-community me-2" />
              Company Details
            </div>
            <div className="row g-3">
              <div className="col-md-8">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="companyName"
                    value={selectedCompany?.label || ""}
                    readOnly
                    placeholder="Company"
                  />
                  <label htmlFor="companyName">Company</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="companyType"
                    value={
                      selectedCompany?.type
                        ? selectedCompany.type.charAt(0).toUpperCase() +
                          selectedCompany.type.slice(1)
                        : ""
                    }
                    readOnly
                    placeholder="Company Type"
                  />
                  <label htmlFor="companyType">Company Type</label>
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-head">
              <i className="ti ti-id-badge-2 me-2" />
              Basic Information
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-floating">
                  <input
                    type="text"
                    className="form-control"
                    id="leaveName"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    placeholder="Leave Type Name"
                    required
                    disabled={isProcessing}
                  />
                  <label htmlFor="leaveName" className="required">
                    Leave Type Name
                  </label>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    required
                    disabled={isProcessing}
                  >
                    <option value="planned">Planned</option>
                    <option value="unplanned">Unplanned</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="other">Other</option>
                  </select>
                  <label htmlFor="category" className="required">
                    Category
                  </label>
                </div>
              </div>
              {formData.category === "other" && (
                <div className="col-12">
                  <div className="unlimited-badge">
                    <i className="ti ti-infinity" />
                    <span>
                      This leave type allows unlimited leaves. Balance and
                      carry forward settings are disabled.
                    </span>
                  </div>
                </div>
              )}

              <div className="col-md-3">
                <div className="form-floating">
                  <input
                    type="number"
                    className="form-control"
                    id="balance"
                    name="balance"
                    value={formData.balance}
                    onChange={handleFormChange}
                    placeholder="Yearly Balance (days)"
                    min={0}
                    required
                    disabled={isProcessing || formData.category === "other"}
                  />
                  <label htmlFor="balance" className="required">
                    Yearly Balance (days)
                  </label>
                </div>
                <div className="form-hint">
                  {formData.category === "other"
                    ? "*Unpaid leaves for 'Other' category"
                    : "Total leaves allocated per year"}
                </div>
              </div>

              <div className="col-12">
                <div className="form-floating">
                  <textarea
                    className="form-control"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Description"
                    style={{ height: 100 }}
                    disabled={isProcessing}
                  />
                  <label htmlFor="description" className="required">
                    Description
                  </label>
                </div>
                <div className="form-hint">
                  Keep it short &amp; action-oriented (e.g., "Paid leave for
                  planned vacations").
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-head">
              <i className="ti ti-calendar-stats me-2" />
              Monthly Accrual Settings
            </div>

            <div className="row g-3">
              <div className="col-12">
                <div
                  className={`switch-card ${
                    formData.isMonthlyAccrual ? "active" : ""
                  } ${formData.category === "other" ? "opacity-50" : ""}`}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ti ti-calendar-repeat" />
                      <div>
                        <div className="switch-title">
                          Enable Monthly Accrual
                        </div>
                        <div className="switch-sub">
                          {formData.category === "other"
                            ? "Not available for 'Other' category"
                            : "Allocate leaves monthly instead of yearly upfront"}
                        </div>
                      </div>
                    </div>
                    <div className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="isMonthlyAccrual"
                        checked={formData.isMonthlyAccrual}
                        onChange={handleFormChange}
                        disabled={
                          isProcessing || formData.category === "other"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {formData.isMonthlyAccrual && formData.category !== "other" && (
                <>
                  <div className="col-12">
                    <div className="info-box">
                      <div className="d-flex align-items-start">
                        <div className="info-box-icon">
                          <i className="ti ti-info-circle" />
                        </div>
                        <div>
                          <strong>How it works:</strong> Leaves are credited
                          at the beginning of each month. Users can only
                          apply for leaves up to their monthly limit.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyAllocation"
                        name="monthlyAllocation"
                        value={formData.monthlyAllocation}
                        onChange={handleFormChange}
                        placeholder="Monthly Allocation"
                        min={0}
                        required={formData.isMonthlyAccrual}
                        disabled={isProcessing}
                      />
                      <label
                        htmlFor="monthlyAllocation"
                        className="required"
                      >
                        Monthly Allocation
                      </label>
                    </div>
                    <div className="form-hint">
                      Leaves credited each month (e.g., 2)
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="form-floating">
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyLimit"
                        name="monthlyLimit"
                        value={formData.monthlyLimit}
                        onChange={handleFormChange}
                        placeholder="Monthly Limit"
                        min={0}
                        required={formData.isMonthlyAccrual}
                        disabled={isProcessing}
                      />
                      <label htmlFor="monthlyLimit" className="required">
                        Monthly Limit
                      </label>
                    </div>
                    <div className="form-hint">
                      Max leaves usable per month (e.g., 2)
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div
                      className={`switch-card ${
                        formData.monthlyCarryForward ? "active" : ""
                      }`}
                    >
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                          <i className="ti ti-arrow-right-circle" />
                          <div>
                            <div className="switch-title">
                              Monthly Carry Forward
                            </div>
                            <div className="switch-sub">
                              Unused monthly leaves roll to next month
                            </div>
                          </div>
                        </div>
                        <div className="form-check form-switch m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="monthlyCarryForward"
                            checked={formData.monthlyCarryForward}
                            onChange={handleFormChange}
                            disabled={isProcessing}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="section-card">
            <div className="section-head">
              <i className="ti ti-users me-2" />
              Eligibility
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="eligibleGender"
                    name="eligibleGender"
                    value={formData.eligibleGender}
                    onChange={handleFormChange}
                    required
                    disabled={isProcessing}
                  >
                    <option value="any">Any</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <label htmlFor="eligibleGender" className="required">
                    Eligible Gender
                  </label>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-floating">
                  <select
                    className="form-select"
                    id="eligibleEmploymentType"
                    name="eligibleEmploymentType"
                    value={formData.eligibleEmploymentType}
                    onChange={handleFormChange}
                    required
                    disabled={isProcessing}
                  >
                    <option value="any">Any</option>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                    <option value="intern">Intern</option>
                  </select>
                  <label
                    htmlFor="eligibleEmploymentType"
                    className="required"
                  >
                    Employment Type
                  </label>
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-floating">
                  <input
                    type="number"
                    className="form-control"
                    id="minimumServiceMonths"
                    name="minimumServiceMonths"
                    value={formData.minimumServiceMonths}
                    onChange={handleFormChange}
                    placeholder="Minimum Service Months"
                    min={0}
                    required
                    disabled={isProcessing}
                  />
                  <label
                    htmlFor="minimumServiceMonths"
                    className="required"
                  >
                    Minimum Service (months)
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-head">
              <i className="ti ti-checkbox me-2" />
              Rules &amp; Toggles
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <div
                  className={`switch-card ${
                    formData.requiresApproval ? "active" : ""
                  }`}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ti ti-stamp" />
                      <div>
                        <div className="switch-title">Requires Approval</div>
                        <div className="switch-sub">
                          Manager approval before leave
                        </div>
                      </div>
                    </div>
                    <div className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="requiresApproval"
                        checked={formData.requiresApproval}
                        onChange={handleFormChange}
                        disabled={isProcessing}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div
                  className={`switch-card ${
                    formData.carryForwardAllowed ? "active" : ""
                  } ${formData.category === "other" ? "opacity-50" : ""}`}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ti ti-arrow-forward-up" />
                      <div>
                        <div className="switch-title">
                          Yearly Carry Forward
                        </div>
                        <div className="switch-sub">
                          {formData.category === "other"
                            ? "Not available for 'Other' category"
                            : "Unused days move to next year"}
                        </div>
                      </div>
                    </div>
                    <div className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="carryForwardAllowed"
                        checked={formData.carryForwardAllowed}
                        onChange={handleFormChange}
                        disabled={
                          isProcessing || formData.category === "other"
                        }
                      />
                    </div>
                  </div>
                </div>
                {formData.carryForwardAllowed &&
                  formData.category !== "other" && (
                    <div className="mt-3">
                      <div className="form-floating">
                        <input
                          type="number"
                          className="form-control"
                          id="carryForwardLimit"
                          name="carryForwardLimit"
                          value={formData.carryForwardLimit}
                          onChange={handleFormChange}
                          min={0}
                          placeholder="Carry Forward Limit (days)"
                          disabled={isProcessing}
                        />
                        <label htmlFor="carryForwardLimit">
                          Carry Forward Limit (days)
                        </label>
                      </div>
                      <div className="form-hint mt-1">
                        Max days that can roll over to next year (e.g., 40)
                      </div>
                    </div>
                  )}
              </div>

              <div className="col-md-4">
                <div
                  className={`switch-card ${
                    formData.isActive ? "active" : ""
                  }`}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ti ti-bolt" />
                      <div>
                        <div className="switch-title">Active</div>
                        <div className="switch-sub">
                          Make this leave type usable
                        </div>
                      </div>
                    </div>
                    <div className="form-check form-switch m-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleFormChange}
                        disabled={isProcessing}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </CommonModal>
    </>
  );
};

export default LeaveTypesTab;
